<template>
  <scroll-area>
    <template slot="body">
      <v-layout fill-height justify-start>
          <v-treeview
            v-model="tree"
            disable-resize-watcher="true"
            style="cursor: pointer"
            :items="nodes"
            :active.sync="treeActive"
            :open.sync="treeOpened"
            :item-children="itemChildren"
            loading-icon='$vuetify.icons.loading'
            active-class="secondary lighten-5"
            class="caption font-weight-medium font-condensend"
            item-key="path"
            return-object
            :load-children="loadChildren"
            selection-type="leaf"
            expand-icon="mdi-chevron-down"
            on-icon="mdi-shield"
            off-icon="mdi-shield-outline"
            indeterminate-icon="mdi-shield-plus"
          >
             <template v-slot:prepend="{ item, open, active }">
              <v-icon @click="() => {clickHandler(item)}">{{ getStatusIcon(item) }}</v-icon>
            </template>

            <template v-slot:label="{ item, open }">
              <span @click="() => {consumerClickHandler(item)}" class="caption font-weight-medium font-condensend">{{
                item.name + ' ' + item.type + `${checkConsumers(item)}`
              }}</span>
            </template>

          </v-treeview>

      </v-layout>

    </template>

  </scroll-area>

</template>

<script lang="ts">
import Vue from 'vue'
import scrollArea from '@/nms/components/partials/scrollarea.vue'

// Типы
interface ResourceItem {
  $id?: string
  id?: string
  path?: string
  name?: string
  cls?: string
  children?: ResourceItem[]
  consumers?: Array<{
    id?: string
    name?: string
    template?: string
    resource?: string
    [key: string]: any
  }>
  [key: string]: any
}

interface StatusesMap {
  [key: string]: string
}

export default Vue.extend({
  name: 'RESOURCES_TREE',

  components: {
    scrollArea,
  },

  props: {
    nodes: {
      type: Array as () => ResourceItem[],
      default: () => []
    },
    itemChildren: {
      type: String,
      default: () => 'items'
    },
    subscribe: {
      type: Boolean,
      default: () => true
    },
    selectable: {
      type: Boolean,
      default: () => false
    }
  },

  data(): {
    selected: ResourceItem[]
    selectedObj: any | null
    statuses: StatusesMap
    tree: ResourceItem[]
    treeActive: ResourceItem[]
    treeOpened: ResourceItem[]
  } {
    return {
      selected: [],
      selectedObj: null,
      statuses: {},
      tree: [],
      treeActive: [],
      treeOpened: []
    }
  },

  watch: {
    selected: {
      handler(newVal: ResourceItem[]): void {
        if (newVal?.length) {
          this.$emit('selected', this.selectedObj)
        } else {
          this.$emit('selected', null)
        }
      },
      deep: true
    },
    nodes: {
      handler(newVal: ResourceItem[]): void {
        this.setup()
      },
      deep: true
    },
  },

  computed: {
    getNodes(): ResourceItem[] {
      return this.nodes
    },
  },

  created(): void {
    console.log(this.$options.name, 'created', this.nodes)
    this.setup()
  },

  beforeDestroy(): void {
    if (this.subscribe) {
      this.statusUnsubscribe(this.$options.name || 'RESOURCES_TREE')
    }
  },

  methods: {
    setup(): void {
      if (this.subscribe) {
        this.statusUnsubscribe(this.$options.name || 'RESOURCES_TREE')
        this.statusSubscribe(this.nodes, this.$options.name || 'RESOURCES_TREE')
        this.loadMirrors()
      }
    },

    async loadMirrors(): Promise<void> {
      if (this.nodes) {
        this.statuses = {}
        this.getMirror(this.nodes)
      }
    },

    getMirror(arr: ResourceItem[]): void {
      for (const item of arr) {
        if (item.$id) {
          this.$store.dispatch('addStatusKey', item)
        }
        if (item.children?.length) {
          this.getMirror(item.children)
        }
      }
    },

    async loadChildren(item: ResourceItem): Promise<void> {
      if (item.consumers?.length) {
        const c$ = item.consumers.find(consumer => consumer.resource === item.path)
        if (!c$) {
          this.$emit('loadchildren', item)
        }
      } else {
        this.$emit('loadchildren', item)
      }
    },

    makeHeap(arr: ResourceItem[]): ResourceItem[] {
      const result: ResourceItem[] = []
      const mapChildren = (arr_: ResourceItem[]) => {
        arr_.forEach(item => {
          result.push(item)
          if (item.children?.length) {
            mapChildren(item.children)
          }
        })
      }
      mapChildren(arr)
      return result
    },

    async statusSubscribe(arr: ResourceItem[], group: string): Promise<void> {
      const heap = this.makeHeap(arr)
      this.$store.dispatch('measuresSubscribeInit', {
        name: 'addStatus',
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
      if (heap.length) {
        await this.$bridge.subscribe(heap.map(obj => obj.$id))
      }
    },

    subscribeHandler(e: { $id?: string; [key: string]: any }): void {
      if (e.$id) {
        this.$store.dispatch('addStatus', e)
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },

    clickHandler(val: ResourceItem): void {
      if (!this.selectable) return
      
      const i$ = this.selected.indexOf(val)
      if (i$ < 0) {
        this.$set(this.selected, this.selected.length, val)
      } else {
        this.selected.splice(i$, 1)
      }
      this.$emit('selectedResouces', this.selected)
    },

    checkConsumers(item: ResourceItem): string {
      let response = ''
      if (item.consumers?.length) {
        const c$ = item.consumers.find(consumer => consumer.resource === item.path || consumer.resource === item.id)
        if (c$) {
          response = ` | ${c$.name} ${c$.template || ''}`
        } else {
          response = ` | ${this.$getTranslate('occupied by underlying structures')}`
        }
      }
      return response
    },

    async consumerClickHandler(item: ResourceItem): Promise<void> {
      try {
        if (item.consumers?.[0]?.id) {
          this.$emit('select', item.consumers[0].id)
        }
        if (this.selectable) return
        
        const consumerInfo = this.checkConsumers(item)
        if (consumerInfo.includes('$')) {
          const active$ = await this.$bridge.getObjectById(item.consumers[0].id)
          if (active$) {
            // Ищем индекс в logical.logical
            const newActiveGroupIndex = this.$store.state.logical?.logical?.findIndex(
              (el: any) => el.name === active$.cls
            )
          }
        }
      } catch (error) {
        console.error(this.$options.name, 'consumerClickHandler', 'error', error)
      }
    },

    getStatusIcon(item: ResourceItem): string {
      if (this.selected.includes(item)) {
        return 'mdi-shield'
      }
      return 'mdi-shield-outline'
    },
  },
})
</script>

<style lang="stylus">

</style>
